import { AfterViewInit, Component, Input, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/auth/auth.service";
import { AlertController, Events, Platform, Tabs } from "ionic-angular";
import { AssessmentDataService } from "../../../../../providers/asmt-data-service/asmt-data-service";
import { CanvasService } from "../../../canvas/canvas-service.interface";
import { NewWoundLocationComponent } from "../new-wound-location/new-wound-location";
import { NewWoundNotesComponent } from "../new-wound-notes/new-wound-notes";
import { NewWoundScoreComponent } from "../new-wound-score/new-wound-score";
import { canvas, NewWoundData } from "../new-wound-site-service";
import { NewWoundSummaryComponent } from "../new-wound-summary/new-wound-summary";
import { NewWoundService } from "../new-wound.service";

/**
 * Generated class for the PhysicianSearchComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: "new-wound-site-tabs",
  templateUrl: "new-wound-site-tabs.html",
})
export class NewWoundSiteTabsComponent implements AfterViewInit {
  @ViewChild("issuesTabs") tabRef: Tabs;
  @Input() config;
  @Input() assessmentId;
  @Input() sectionId;
  @Input() canvasService: CanvasService;
  tab1Root = NewWoundLocationComponent;
  tab2Root = NewWoundScoreComponent;
  tab3Root = NewWoundNotesComponent;
  tab4Root = NewWoundSummaryComponent;
  tab1Params: {};
  @ViewChild("issuesTabs") issuesTabs: Tabs;
  carryData: Array<{}> = [];
  subscriptions: Array<any> = [];
  currentWound: NewWoundData;
  nextLocationIndex: number = 1;
  tabIndex: number = 0;
  cancelPoint: boolean = true;
  enableStepOne = true;
  enableStepTwo = false;
  enableStepThree = false;
  enableStepFour = false;
  readOnly: boolean = false;
  openSavedToggle: boolean = true;

  constructor(
    private newWoundService: NewWoundService,
    public events: Events,
    public dataService: AssessmentDataService,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    public platform: Platform,
    private authSvc: AuthService
  ) {}

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.newWoundService.getCurrentPointData().subscribe((wound) => {
        if (wound) {
          this.currentWound = wound;
        }
      })
    );
  }

  ngOnInit(): void {
    this.readOnly = this.config.readonly;
    this.tab1Params = {
      config: this.config,
    };
    this.events.subscribe("defaultTab", (data) => {
      if (data) {
        this.cancelPoint = true;
        this.openSavedToggle = true;
      }
    });

    this.events.publish("getTabIndex", this.tabIndex);
  }

  @Input() set data(incomingData: any) {
    if (!incomingData) {
      let newWoundData: NewWoundData = this.defaultWoundData(0, "body");
      this.canvasService.addPointData({
        x: undefined,
        y: undefined,
        category: "body",
        locationIndex: 0,
        active: true,
        woundStatus: "inprogress",
      });
      this.canvasService.selectPoint(0);
      this.newWoundService.setCurrentPointData(newWoundData);
      this.newWoundService.pushTheCurrentDataToArr(newWoundData);
    } else if (this.openSavedToggle) {
      // Do not refresh after a save/edit. Instead, keep showing summary.
      this.canvasService.removePoint(0);
      // Find max locationIndex and add 1 (handles gaps in locationIndex)
      const maxIndex = incomingData.reduce((max, item) => {
        if (item && typeof item.locationIndex === "number") {
          return Math.max(max, item.locationIndex);
        }
        return max;
      }, -1);
      this.nextLocationIndex = maxIndex + 1;
      this.newWoundService.setAllDataObservable(incomingData);
      let woundData = incomingData[incomingData.length - 1];
      if (!this.readOnly) {
        woundData = this.defaultWoundData(
          this.nextLocationIndex,
          woundData.stepOneSidebarData.locationType
        );
        this.canvasService.addPointData({
          x: undefined,
          y: undefined,
          category: "body",
          locationIndex: this.nextLocationIndex,
          active: true,
          woundStatus: "inprogress",
        });
        this.canvasService.selectPoint(
          this.canvasService["dataSet"].length - 1
        );
        this.newWoundService.pushTheCurrentDataToArr(woundData);
      }
      this.newWoundService.setCurrentPointData(woundData);
    }
  }

  defaultWoundData(index: number, locationType: string): NewWoundData {
    let newCanvas: canvas = {
      status: "imputed",
    };

    let newWoundData: NewWoundData = {
      stepOneSidebarData: {
        locationTitle: "",
        locationType: locationType,
        woundType: "",
        woundTypeOther: "",
        width: "",
        length: "",
        depth: "",
        unableToMeasure: false,
        tracking: false,
        trackingOther: "",
        undermining: false,
        underminingOther: "",
        stage: "",
        exudateLevel: "",
        exudateLevelOther: "",
        consistency: "",
        consistencyOther: "",
        drainageType: "",
        drainageTypeOther: "",
        toolName: "",
        severity: null,
        migrated: false,
      },
      canvasData: newCanvas,
      locationIndex: index,
      recordStatus: "open",
      woundStatus: "inprogress",
      isReassessed: false,
      reAccessedVisitId: "",
      notes: { woundCareTreatment: "", additionalNotes: "" },
      score: { tool: "", value: null },
    };
    return newWoundData;
  }

  addNewWound() {
    this.openSavedToggle = true;
    this.cancelPoint = true;
    // Find max locationIndex and add 1 (handles gaps and duplicates)
    const maxIndex = this.canvasService["dataSet"].reduce((max, item) => {
      if (item && typeof item.locationIndex === "number") {
        return Math.max(max, item.locationIndex);
      }
      return max;
    }, -1);
    this.nextLocationIndex = maxIndex + 1;
    let newWoundData: NewWoundData = this.defaultWoundData(
      this.nextLocationIndex,
      "body"
    );
    this.canvasService.addPointData({
      x: undefined,
      y: undefined,
      category: "body",
      locationIndex: this.nextLocationIndex,
      active: true,
      woundStatus: "inprogress",
    });
    this.canvasService.selectPoint(this.canvasService["dataSet"].length - 1);
    this.newWoundService.setCurrentPointData(newWoundData);
    this.newWoundService.pushTheCurrentDataToArr(newWoundData);
    this.issuesTabs.select(0);
  }

  saveWound() {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("woundStatusAfterAssessinng"),
      cssClass: "alert-woundsite",
      buttons: [
        {
          text: "healed",
          cssClass: this.platform.is("ios")
            ? "alert-greenbtn"
            : "alert-paddingright100 alert-greenbtn text-captilize",
          handler: () => {
            this.saveOrEditData("healed");
          },
        },
        {
          text: "active",
          cssClass: "text-captilize",
          handler: () => {
            this.saveOrEditData("active");
          },
        },
      ],
    });
    alert.present();
  }

  // SUP-19942: record WHO documented this specific entry, at the moment it is
  // saved. Nothing else in the payload can answer that question per entry: the
  // record envelope's `addedBy` is sticky to the wound's first documenter
  // (Entity#populateJsonParametersDiffThanId only sets it when absent), and the
  // audit columns the backend injects are re-stamped section-wide on every save.
  // Stamping inside `value` is the same mechanism reAccessedVisitId already uses,
  // and `value` is the only part of the record that survives into the history
  // payload (AssessmentDetailServiceImpl#mapAssessmentWithVisits).
  private stampDocumentingClinician() {
    const user = this.authSvc.getUser();
    if (!user) {
      return;
    }
    this.currentWound.documentedBy = user.name;
    this.currentWound.documentedById = user.qbosUserId || user.userId;
  }

  saveOrEditData(status) {
    if (this.dataService.isAssessmentCompleted()) {
      this.alertCtrl
        .create({
          title: this.translatePipe.instant("woundStatusLockedTitle"),
          message: this.translatePipe.instant("woundStatusLockedMessage"),
          buttons: [this.translatePipe.instant("ok")],
        })
        .present();
      return;
    }
    if (
      this.currentWound.canvasData &&
      this.currentWound.canvasData.x != undefined &&
      this.currentWound.canvasData.y != undefined
    ) {
      this.openSavedToggle = false;
      this.cancelPoint = false;
      this.currentWound.woundStatus = status;
      this.currentWound.reAccessedVisitId = this.assessmentId;
      this.currentWound.recordStatus = "saved";
      this.stampDocumentingClinician();
      let finalData = { value: this.currentWound };
      if (this.currentWound.recordId) {
        // edit
        this.dataService
          .editRecord(
            this.assessmentId,
            this.sectionId,
            this.config.id,
            this.currentWound.recordId,
            finalData
          )
          .subscribe(
            (returnData) => {
              this.newWoundService.updateOrPushCurrentPoint(returnData.value);
              this.setPoint();
              this.events.publish("refreshSection");
            },
            (err) => {
              // console.log( "Error during data edit in wound ", err );
            }
          );
      } else {
        // create
        this.dataService
          .storeRecord(
            this.assessmentId,
            this.sectionId,
            this.config.id,
            finalData,
            this.config.cardName
          )
          .subscribe(
            (data) => {
              if (data) {
                this.newWoundService.updateOrPushCurrentPoint({
                  ...data.value,
                  recordId: data.id,
                });
                this.setPoint();
                this.events.publish("refreshSection");
              }
            },
            (err) => {
              // console.log( "Error during data saved in wound ", err );
            }
          );
      }
    }
  }

  ngOnDestroy() {
    for (let sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }

  changeTab() {
    this.tabIndex = this.tabRef.getSelected().index;
    if (this.tabIndex === 0) {
      this.enableStepOne = true;
      this.enableStepTwo = false;
      this.enableStepThree = false;
      this.enableStepFour = false;
    } else if (this.tabIndex === 1) {
      this.enableStepOne = false;
      this.enableStepTwo = true;
      this.enableStepThree = false;
      this.enableStepFour = false;
    } else if (this.tabIndex === 2) {
      this.enableStepOne = false;
      this.enableStepTwo = false;
      this.enableStepThree = true;
      this.enableStepFour = false;
    } else if (this.tabIndex === 3) {
      this.enableStepOne = false;
      this.enableStepTwo = false;
      this.enableStepThree = false;
      this.enableStepFour = true;
    }
    this.events.publish("getTabIndex", this.tabIndex);
  }

  previousTab() {
    this.tabIndex = this.tabRef.getSelected().index;
    if (this.tabIndex <= 3 && this.tabIndex >= 0) {
      this.tabIndex = this.tabIndex - 1;
      this.tabRef.select(this.tabIndex);
    }
    this.events.publish("getTabIndex", this.tabIndex);
  }

  nextTab() {
    this.tabIndex = this.tabRef.getSelected().index;
    if (this.tabIndex < 3 && this.tabIndex >= 0) {
      this.tabIndex = this.tabIndex + 1;
      this.tabRef.select(this.tabIndex);
    }
    this.events.publish("getTabIndex", this.tabIndex);
  }

  goToWoundLocation() {
    this.issuesTabs.select(0);
  }

  setPoint() {
    let array = this.canvasService["dataSet"];
    for (let i in array) {
      if (array[i].locationIndex === this.currentWound.locationIndex) {
        array[i].woundStatus = this.currentWound.woundStatus;
        break;
      }
    }
  }

  checkValidation() {
    let next = false;
    if (this.tabRef.getSelected()) {
      let tabIndex = this.tabRef.getSelected().index;
      for (let i = 0; i < this.config.tabs.length; i++) {
        if (this.config.tabs[i].locationOfWound) {
          for (let j = 0; j < this.config.tabs[i].locationOfWound.length; j++) {
            if (this.config.tabs[i].locationOfWound[j].sidebar) {
              //Validation For Step One
              if (tabIndex === 0) {
                for (var property in this.config.tabs[i].locationOfWound[j]
                  .sidebar) {
                  if (property === "sizeMeasurements") {
                    next = this.validateNestedFields(
                      this.config.tabs[i].locationOfWound[j].sidebar,
                      property
                    );
                    if (next === true) {
                      return next;
                    }
                  } else {
                    next = this.validateFields(
                      this.config.tabs[i].locationOfWound[j].sidebar,
                      property
                    );
                    if (next === true) {
                      return next;
                    }
                  }
                }
              }
            }
          }
        } else if (this.config.tabs[i].score) {
          //Validation For Step three
          if (tabIndex === 1) {
            if (
              this.currentWound.stepOneSidebarData.toolName &&
              this.currentWound.stepOneSidebarData.toolName.length > 0
            ) {
              let value = this.currentWound.score
                ? this.currentWound.score.value
                : null;
              if (value === null) {
                return (next = true);
              }
            }
          }
        } else if (this.config.tabs[i].notes) {
          //Validation For Step three
          if (tabIndex === 2) {
            for (var property in this.config.tabs[i].notes[0]) {
              next = this.validateFields(
                this.config.tabs[i].notes[0],
                property
              );
              if (next === true) {
                return next;
              }
            }
          }
        }
      }
    }
    return next;
  }

  validateFields(config, property) {
    let next = false;
    let prop = config[property];
    if (prop.required) {
      if (this.tabIndex === 0) {
        if (this.currentWound.stepOneSidebarData[property].length === 0) {
          return (next = true);
        }
        if (this.currentWound.canvasData && !this.currentWound.canvasData.x) {
          return (next = true);
        }
        if (this.currentWound.canvasData && !this.currentWound.canvasData.y) {
          return (next = true);
        }
      } else if (this.tabIndex === 2) {
        if (this.currentWound.notes[property].length === 0) {
          return (next = true);
        }
      }
    }

    let min = prop.minLength ? prop.minLength : prop.min;
    if (min) {
      if (this.tabIndex === 0) {
        if (this.currentWound.stepOneSidebarData[property].length < min) {
          return (next = true);
        }
      } else if (this.tabIndex === 2) {
        if (this.currentWound.notes[property].length < min && prop.required) {
          return (next = true);
        }
      }
    }

    let max = prop.maxLength ? prop.minLength : prop.max;
    if (max) {
      if (this.tabIndex === 0) {
        if (this.currentWound.stepOneSidebarData[property].length > max) {
          return (next = true);
        }
      } else if (this.tabIndex === 2) {
        if (this.currentWound.notes[property].length > max) {
          return (next = true);
        }
      }
    }

    if (prop.supportOther && prop.otherRules) {
      if (prop.otherRules.maxLength) {
        if (this.tabIndex === 0) {
          if (
            this.currentWound.stepOneSidebarData[property + "Other"].length >
            prop.otherRules.maxLength
          ) {
            return (next = true);
          }
        }
      }
    }
    return next;
  }

  validateNestedFields(config, property) {
    let next = false;
    let count = 0;
    let nestedProperties = config[property];
    for (let nestedProp in nestedProperties[0]) {
      let prop = nestedProperties[0][nestedProp];
      if (prop.requiredIfOtherEmpty) {
        if (this.tabIndex === 0) {
          if (!this.currentWound.stepOneSidebarData[nestedProp]) {
            count++;
          }

          if (
            this.currentWound.stepOneSidebarData[nestedProp].length >
            prop.maxLength + 4
          ) {
            count++;
          }
        }
      }
      if (count === 4) {
        return (next = true);
      }
    }
    return next;
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/new-wound-site-card/new-wound-site-tabs/new-wound-site-tabs.ts